import React, {createContext, ReactNode, useCallback, useContext, useEffect, useState} from 'react';
import {
    closeSiteToday,
    disableDeliveryApi,
    enableDeliveryApi,
    getDeliveryStatus,
    isSiteClosed,
    openSiteToday,
    resetOverrides
} from "../api/api";
import {useAuth} from "./auth.context";
import {useNotificationContext} from "./notification.context";

interface SiteProps {
    isClosed: boolean;
    // Courier delivery is switched off separately from the whole site, for the
    // days delivery is unavailable - self-pickup keeps working
    isDeliveryDisabled: boolean;
    checkIfSiteClosed: () => void;
    checkDeliveryStatus: () => void;
    closeSite: () => void;
    openSite: () => void;
    resetOpening: () => void;
    enableDelivery: () => void;
    disableDelivery: () => void;
}

const SiteContext = createContext<SiteProps | null>(null);

export const useSiteContext = () => {
    const context = useContext(SiteContext);
    if (!context) {
        throw new Error('useSiteContext must be used within a SiteProvider');
    }
    return context;
};

const SiteProvider: React.FC<{ children: ReactNode }> = ({children}) => {
    const [isClosed, setIsClosed] = useState(false);
    const [isDeliveryDisabled, setIsDeliveryDisabled] = useState(false);

    const {token, isLoggedIn, logout} = useAuth();
    const {addNotification} = useNotificationContext();

    const checkIfSiteClosed = useCallback(async () => {
        const isClosed = await isSiteClosed();

        setIsClosed(Boolean(isClosed));
    }, [])

    const checkDeliveryStatus = useCallback(async () => {
        const status = await getDeliveryStatus().catch(() => null);

        if (status) {
            setIsDeliveryDisabled(Boolean(status.isDeliveryDisabled));
        }
    }, [])

    const openSite = useCallback(async () => {
        if (!isLoggedIn) {
            return;
        }

        openSiteToday(token).catch((err) => {
            addNotification('Wystąpił błąd');
        });

        setTimeout(() => checkIfSiteClosed(), 1000);
    }, [token, isLoggedIn, checkIfSiteClosed])

    const closeSite = useCallback(async () => {
        if (!isLoggedIn) {
            return;
        }

        closeSiteToday(token).catch(() => {
            addNotification('Wystąpił błąd');
        });

        setTimeout(() => checkIfSiteClosed(), 1000);
    }, [token, isLoggedIn, checkIfSiteClosed])

    // The toggle endpoints answer with the state they stored, so the panel can
    // reflect it straight away instead of polling for it
    const setDelivery = useCallback(async (disabled: boolean) => {
        if (!isLoggedIn) {
            return;
        }

        const status = await (disabled ? disableDeliveryApi(token) : enableDeliveryApi(token))
            .catch(() => {
                addNotification('Wystąpił błąd');
                return null;
            });

        if (status) {
            setIsDeliveryDisabled(Boolean(status.isDeliveryDisabled));
        }
    }, [token, isLoggedIn, addNotification])

    const enableDelivery = useCallback(() => setDelivery(false), [setDelivery])
    const disableDelivery = useCallback(() => setDelivery(true), [setDelivery])

    useEffect(() => {
        checkIfSiteClosed()
        checkDeliveryStatus()
    }, []);

    const resetOpening = useCallback(async () => {
        if (!isLoggedIn) {
            return;
        }

        resetOverrides(token).catch(() => {
            addNotification('Wystąpił błąd');
        });

        setTimeout(() => checkIfSiteClosed(), 1000);
    }, [token, isLoggedIn, checkIfSiteClosed])

    return (
        <SiteContext.Provider
            value={{
                isClosed,
                isDeliveryDisabled,
                checkIfSiteClosed,
                checkDeliveryStatus,
                openSite,
                closeSite,
                resetOpening,
                enableDelivery,
                disableDelivery
            }}>
            {children}
        </SiteContext.Provider>
    );
};

export default SiteProvider;
